import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Text, Button, makeStyles, tokens } from '@fluentui/react-components';
import { HomeRegular, ArrowLeftRegular } from '@fluentui/react-icons';
import DrillDownView from '../DrillDownView';
import IssueList from '../IssueList';
import Breadcrumb from '../Breadcrumb';
import { fetchIssues } from '../../services/api';
import { Issue, DrillDownItem, BreadcrumbItem } from '../../types';

interface CategoryViewProps {
  apiUrl?: string;
  onNavigate?: (path: string) => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    height: 'calc(100vh - 2rem)',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL,
  },
  leftColumn: {
    width: '68%',
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingRight: tokens.spacingVerticalL,
    overflowY: 'auto',
  },
  rightColumn: {
    width: '32%',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: tokens.spacingVerticalM,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80vh',
    gap: tokens.spacingVerticalM,
  },
});

const CategoryView: React.FC<CategoryViewProps> = ({ apiUrl, onNavigate }) => {
  const { domain, category } = useParams<{ domain: string; category: string }>();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const styles = useStyles();

  useEffect(() => {
    const loadIssues = async (): Promise<void> => {
      try {
        const data = await fetchIssues();
        setIssues(data);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load issues:', error);
        setLoading(false);
      }
    };
    loadIssues();
  }, []);

  const subcategories = useMemo<DrillDownItem[]>(() => {
    if (!domain || !category) return [];
    const subcategorySet = new Set<string>();
    issues
      .filter(
        (issue) =>
          issue.Taxonomy.Domain === decodeURIComponent(domain) &&
          issue.Taxonomy.Category === decodeURIComponent(category)
      )
      .forEach((issue) => {
        subcategorySet.add(issue.Taxonomy.Subcategory);
      });
    return Array.from(subcategorySet)
      .map((subcategory) => ({
        label: subcategory,
        value: subcategory,
        count: issues.filter(
          (i) =>
            i.Taxonomy.Domain === decodeURIComponent(domain) &&
            i.Taxonomy.Category === decodeURIComponent(category) &&
            i.Taxonomy.Subcategory === subcategory
        ).length,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [issues, domain, category]);

  const handleSubcategoryClick = (subcategory: DrillDownItem): void => {
    if (!domain || !category) return;
    const path = `/${encodeURIComponent(domain)}/${encodeURIComponent(category)}/${encodeURIComponent(subcategory.value)}`;
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const handleBack = (): void => {
    if (!domain) return;
    const path = `/${encodeURIComponent(domain)}`;
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const handleReset = (): void => {
    if (onNavigate) {
      onNavigate('/');
    } else {
      navigate('/');
    }
  };

  const handleIssueClick = (issue: Issue): void => {
    const path = `/${encodeURIComponent(issue.Taxonomy.Domain)}/${encodeURIComponent(issue.Taxonomy.Category)}/${encodeURIComponent(issue.Taxonomy.Subcategory)}/${encodeURIComponent(issue.Taxonomy.Issue)}/${encodeURIComponent(issue.Path)}`;
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const breadcrumbPath: BreadcrumbItem[] =
    domain && category
      ? [
          { label: decodeURIComponent(domain), value: domain },
          { label: decodeURIComponent(category), value: category },
        ]
      : [];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" />
        <Text>Loading issues...</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* LEFT COLUMN - Navigation */}
      <div className={styles.leftColumn}>
        <Breadcrumb path={breadcrumbPath} />
        <div className={styles.headerRow}>
          <Text size={500} weight="semibold">
            {category ? `Subcategories in ${decodeURIComponent(category)}` : 'Subcategories'}
          </Text>
          <div style={{ display: 'flex', gap: tokens.spacingVerticalXS }}>
            <Button
              icon={<ArrowLeftRegular />}
              appearance="subtle"
              onClick={handleBack}
              size="small"
            >
              Back
            </Button>
            <Button
              icon={<HomeRegular />}
              appearance="subtle"
              onClick={handleReset}
              size="small"
            >
              Reset
            </Button>
          </div>
        </div>
        <DrillDownView
          items={subcategories}
          onItemClick={handleSubcategoryClick}
        />
      </div>

      {/* RIGHT COLUMN - Issue List */}
      <div className={styles.rightColumn}>
        <IssueList
          issues={issues}
          currentPath={domain && category ? { domain, category } : undefined}
          onIssueClick={handleIssueClick}
        />
      </div>
    </div>
  );
};

export default CategoryView;
